import { useState } from 'react';
import { Button, Input } from '@vd/ui';
import { AlertTriangle } from 'lucide-react';
import type { UpdateProjectWidgetBody } from '@vd/types';
import styles from './AllowedSitesEditor.module.css';

/** Mirrors MAX_ALLOWED_ORIGINS in the api's widget config. */
export const MAX_ALLOWED_ORIGINS = 25;

/**
 * The origin allowlist editor: which sites may use this widget key.
 *
 * One component, rendered in three places (Install + Security tabs in the
 * full Studio, and the onboarding quick-setup) so the add/remove/dedupe rules
 * can never drift between them. Edits are staged through `onChange` exactly
 * like every other widget field; the caller owns saving.
 */
export function AllowedSitesEditor({
  origins,
  onChange,
  anySiteNow = false,
}: {
  origins: string[];
  onChange: (p: UpdateProjectWidgetBody) => void;
  /**
   * Saved on with no site listed, from before one was required (t-a4DgDT):
   * the one widget that takes reports from any website. Any other with no
   * site cannot be turned on, and the empty list says so instead.
   */
  anySiteNow?: boolean;
}) {
  const [originInput, setOriginInput] = useState('');

  const addOrigin = (): void => {
    const value = originInput.trim();
    if (!value) return;
    // Shape validation lives on the server, which rejects unusable entries
    // with a specific message; duplicating those rules here would create two
    // sources of truth. Duplicates and the cap ARE checked here, because the
    // server silently dedupes (the entry would just vanish after save) and
    // 400s on the cap (a worse way to learn).
    if (origins.includes(value)) {
      setOriginInput('');
      return;
    }
    if (origins.length >= MAX_ALLOWED_ORIGINS) return;
    onChange({ allowedOrigins: [...origins, value] });
    setOriginInput('');
  };

  return (
    // Found by `data-allowed-sites` when a notice's Add your site brings the person here (t-a4DgDT).
    <div className={styles.wrap} data-allowed-sites tabIndex={-1}>
      <h3 className={styles.title}>Allowed sites</h3>
      {origins.length === 0 ? (
        <div className={styles.noticeWarn}>
          <AlertTriangle size={16} aria-hidden />
          <div>
            <strong>{anySiteNow ? 'This widget accepts reports from any website.' : 'No allowed sites yet. The widget works only on the sites listed here, so add yours before turning it on.'}</strong>
            <p className={styles.help}>
              {/* The backend is explicit that the allowlist stops hostile web
                  pages, not scripts — a non-browser client can send any Origin
                  it likes. Promising it "locks down" a stolen key would leave
                  the owner believing a solved problem. */}
              Listing your sites stops other <em>websites</em> from using your key. It cannot stop a
              script posting directly. The captcha and rate limits are what bound that.
            </p>
          </div>
        </div>
      ) : (
        <ul className={styles.originList}>
          {origins.map((origin) => (
            <li key={origin} className={styles.originItem}>
              <code>{origin}</code>
              <button
                type="button"
                className={styles.linkButton}
                onClick={() => onChange({ allowedOrigins: origins.filter((o) => o !== origin) })}
              >
                Remove
              </button>
            </li>
          ))}
        </ul>
      )}

      {origins.length >= MAX_ALLOWED_ORIGINS && (
        <p className={styles.help}>Maximum of {MAX_ALLOWED_ORIGINS} sites reached.</p>
      )}
      <div className={styles.addRow}>
        <Input
          aria-label="Site to allow"
          placeholder="https://app.acme.com or https://*.acme.com"
          value={originInput}
          onChange={(e) => setOriginInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addOrigin(); } }}
        />
        <Button variant="secondary" onClick={addOrigin}>Add site</Button>
      </div>
    </div>
  );
}
